import {
  type QSetReviewVerdictOutput,
  useGetQSetTaskReviewSummaryQuery,
  Verdict,
} from '@exam-management/graphql';
import {
  BaseQSetReviewResultList,
  QSetReviewResultOverviewCardGroup,
  QSetReviewSubtaskReviewResult,
  type qSetReviewResultListColumn,
} from '@exam-management/qset-review-subtask-review-result';
import { useNavigate, useParams } from '@exam-management/router';
import { Button, PaperSection, VisibilityOutlinedIcon, Flex } from '@shared/components';
import { matchPath, useLocation } from 'react-router-dom';

export default function QSetReviewTaskSummary() {
  const location = useLocation();
  const matchedTaskManagement = matchPath(
    '/task-management/qset-review/:taskId/*',
    location.pathname,
  );

  const paramPath = matchedTaskManagement
    ? '/task-management/qset-review/:taskId/qset/:qSetId/review-result'
    : '/my-task/qset-review/:taskId/qset/:qSetId/review-result';
  const { taskId, qSetId } = useParams(paramPath);

  const navigate = useNavigate();
  const { data, loading: isLoading } = useGetQSetTaskReviewSummaryQuery({
    variables: {
      getQSetTaskReviewSummaryInput: {
        mainTaskId: taskId,
        verdicts: [Verdict.Rejected, Verdict.Approved],
        qSetId,
      },
    },
    fetchPolicy: 'cache-and-network',
  });

  const getQSetReviewResultsVerdict = (verdicts: QSetReviewVerdictOutput[] | null | undefined) => {
    if (!verdicts) return null;
    const hasApproved = verdicts.some((verdict) => verdict.verdict === Verdict.Approved);
    const hasRejected = verdicts.some((verdict) => verdict.verdict === Verdict.Rejected);
    if (hasApproved && hasRejected) {
      return QSetReviewSubtaskReviewResult.ApprovedPartially;
    } else if (verdicts.length === 0) {
      return null;
    } else if (verdicts.every((verdict) => verdict.verdict === Verdict.Approved)) {
      return QSetReviewSubtaskReviewResult.Approved;
    } else if (verdicts.every((verdict) => verdict.verdict === Verdict.Rejected)) {
      return QSetReviewSubtaskReviewResult.Rejected;
    } else {
      return null;
    }
  };

  const rows: qSetReviewResultListColumn[] =
    data?.wflGetQSetTaskReviewSummary.results.map((result) => {
      return {
        id: result.question.id,
        qid: result.question.questionRefCode,
        reviewResult: getQSetReviewResultsVerdict(result.verdicts),
        comment: result.totalComments,
        order: result.question.order,
        viewDetails: (
          <Button
            size='small'
            variant='outlined'
            color='secondary'
            startIcon={<VisibilityOutlinedIcon />}
            sx={(theme) => ({
              p: theme.spacing(1.5, 3),
              width: '80px',
              maxHeight: '32px',
            })}
            onClick={() =>
              navigate(
                {
                  pathname: matchedTaskManagement
                    ? `/task-management/qset-review/:taskId/qset/:qSetId/review-result/exam-details`
                    : `/my-task/qset-review/:taskId/qset/:qSetId/review-result/exam-details`,

                  search: `?order=${result.question.order}`,
                },
                {
                  params: {
                    taskId,
                    qSetId,
                  },
                },
              )
            }
          >
            View
          </Button>
        ),
      };
    }) ?? [];

  const approvedCount = rows.filter(
    (row) => row.reviewResult === QSetReviewSubtaskReviewResult.Approved,
  ).length;
  const rejectedCount = rows.filter(
    (row) => row.reviewResult === QSetReviewSubtaskReviewResult.Rejected,
  ).length;
  const approvedPartiallyCount = rows.filter(
    (row) => row.reviewResult === QSetReviewSubtaskReviewResult.ApprovedPartially,
  ).length;
  const commentCount = rows.reduce((acc, row) => acc + row.comment, 0);

  return (
    <PaperSection title='Review Summary'>
      <Flex
        sx={(theme) => ({
          padding: theme.spacing(6),
          flexDirection: 'column',
          gap: theme.spacing(4),
        })}
      >
        <QSetReviewResultOverviewCardGroup
          cards={[
            {
              title: 'จำนวนข้อสอบทั้งหมด',
              description: `${rows.length} ข้อ`,
            },
            {
              title: 'เห็นด้วย',
              description: `${approvedCount}`,
              variant: 'success',
            },
            {
              title: 'เห็นด้วยบางส่วน',
              description: `${approvedPartiallyCount}`,
              variant: 'warning',
            },
            {
              title: 'ไม่เห็นด้วย',
              description: `${rejectedCount}`,
              variant: 'error',
            },
            {
              title: 'ความคิดเห็นทั้งหมด',
              description: `${commentCount}`,
              variant: 'info',
            },
          ]}
        />
        <BaseQSetReviewResultList rows={rows} loading={isLoading} />
      </Flex>
    </PaperSection>
  );
}
